import appointment from "../../../../services/appointment";
import { WIZARD_DELAYS } from "../constants";
import { BaseStepDependencies } from "./stepTypes";

interface Availability {
  day_of_week: number;
  start_time: string;
  end_time: string;
}

interface AvailabilityStepDependencies extends BaseStepDependencies {
  addUserMessage: (content: string) => void;
}

const DAYS = [
  { value: 0, label: "Segunda-feira", short: "Seg" },
  { value: 1, label: "Terça-feira", short: "Ter" },
  { value: 2, label: "Quarta-feira", short: "Qua" },
  { value: 3, label: "Quinta-feira", short: "Qui" },
  { value: 4, label: "Sexta-feira", short: "Sex" },
  { value: 5, label: "Sábado", short: "Sáb" },
  { value: 6, label: "Domingo", short: "Dom" },
];

const PRESET_SCHEDULES = [
  {
    name: "Comercial (Seg-Sex 8h-18h)",
    schedule: [0, 1, 2, 3, 4].map((day) => ({
      day_of_week: day,
      start_time: "08:00",
      end_time: "18:00",
    })),
  },
  {
    name: "Manhãs (Seg-Sex 8h-12h)",
    schedule: [0, 1, 2, 3, 4].map((day) => ({
      day_of_week: day,
      start_time: "08:00",
      end_time: "12:00",
    })),
  },
  {
    name: "Tardes (Seg-Sex 14h-18h)",
    schedule: [0, 1, 2, 3, 4].map((day) => ({
      day_of_week: day,
      start_time: "14:00",
      end_time: "18:00",
    })),
  },
];

// Estado temporário
let currentAvailabilities: Availability[] = [];
let currentDay: number | null = null;

export const startAvailabilityConfiguration = (
  deps: AvailabilityStepDependencies,
) => {
  const { addBotMessage } = deps;

  // Reset
  currentAvailabilities = [];
  currentDay = null;

  setTimeout(() => {
    addBotMessage(
      "Vamos configurar seus horários de atendimento! 📅\n\nQuando você está disponível?",
      { wizardType: "info", delay: WIZARD_DELAYS.MEDIUM },
    );
  }, 800);

  setTimeout(() => {
    addBotMessage("Escolha uma das opções rápidas ou configure manualmente:", {
      wizardType: "question",
      delay: WIZARD_DELAYS.MEDIUM,
      actions: [
        ...PRESET_SCHEDULES.map((preset) => ({
          label: preset.name,
          onClick: () => handlePresetSelection(preset.schedule, deps),
          variant: "secondary" as const,
        })),
        {
          label: "Configurar Manualmente",
          onClick: () => startManualConfiguration(deps),
          variant: "primary" as const,
        },
        {
          label: "Pular por Enquanto",
          onClick: () => handleSkipAvailability(deps),
          variant: "secondary" as const,
        },
      ],
    });
  }, WIZARD_DELAYS.LONG);
};

const handlePresetSelection = (
  schedule: Availability[],
  deps: AvailabilityStepDependencies,
) => {
  const { addUserMessage } = deps;

  addUserMessage(
    `${PRESET_SCHEDULES.find((p) => p.schedule === schedule)?.name}`,
  );

  currentAvailabilities = [...schedule];
  saveAvailabilityAndContinue(deps);
};

const startManualConfiguration = (deps: AvailabilityStepDependencies) => {
  const { addUserMessage, addBotMessage } = deps;

  addUserMessage("Configurar manualmente");

  setTimeout(() => {
    addBotMessage("Perfeito! Vamos começar.\n\nEm quais dias você atende?", {
      wizardType: "question",
      delay: WIZARD_DELAYS.MEDIUM,
      actions: DAYS.slice(0, 5).map((day) => ({
        label: day.short,
        onClick: () => handleDaySelection(day.value, deps),
        variant: "secondary" as const,
      })),
    });
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(() => {
    addBotMessage("Fins de semana:", {
      wizardType: "question",
      delay: WIZARD_DELAYS.MEDIUM,
      actions: [
        ...DAYS.slice(5).map((day) => ({
          label: day.label,
          onClick: () => handleDaySelection(day.value, deps),
          variant: "secondary" as const,
        })),
        {
          label: "Finalizar Configuração",
          onClick: () => saveAvailabilityAndContinue(deps),
          variant: "primary" as const,
        },
      ],
    });
  }, WIZARD_DELAYS.LONG);
};

const handleDaySelection = (
  day: number,
  deps: AvailabilityStepDependencies,
) => {
  const { addUserMessage, addBotMessage, setAwaitingUserInput, advanceToStep } =
    deps;

  const dayLabel = DAYS.find((d) => d.value === day)?.label || "";
  addUserMessage(dayLabel);

  currentDay = day;

  setTimeout(() => {
    addBotMessage(
      `Ótimo! ${dayLabel}\n\nQual seu horário de início?\n\nDigite no formato HH:MM (ex: 08:00)`,
      { wizardType: "question", delay: WIZARD_DELAYS.MEDIUM },
    );
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(() => {
    advanceToStep("availability_start", 0);
    setAwaitingUserInput(true);
  }, WIZARD_DELAYS.LONG);
};

export const handleStartTimeInput = (
  time: string,
  deps: AvailabilityStepDependencies,
) => {
  const { addBotMessage, setAwaitingUserInput } = deps;

  // Validação: remove espaços
  const trimmedTime = time.trim();

  // Validação: formato vazio
  if (!trimmedTime) {
    setTimeout(() => {
      addBotMessage(
        "❌ O horário não pode estar vazio. Digite no formato HH:MM (ex: 08:00)",
        {
          wizardType: "error",
          delay: WIZARD_DELAYS.MEDIUM,
        },
      );
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.MEDIUM);
    return;
  }

  // Validação: formato HH:MM
  if (!isValidTimeFormat(trimmedTime)) {
    setTimeout(() => {
      addBotMessage("❌ Formato inválido. Use HH:MM (ex: 08:00 ou 14:30)", {
        wizardType: "error",
        delay: WIZARD_DELAYS.MEDIUM,
      });
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.MEDIUM);
    return;
  }

  // Salva temporariamente
  (
    globalThis as typeof globalThis & { __temp_start_time: string }
  ).__temp_start_time = trimmedTime;

  setTimeout(() => {
    addBotMessage(
      `Perfeito! Início às ${trimmedTime}\n\nE qual o horário de término?\n\nDigite no formato HH:MM (ex: 18:00)`,
      { wizardType: "question", delay: WIZARD_DELAYS.MEDIUM },
    );
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(() => {
    deps.advanceToStep("availability_end", 0);
    setAwaitingUserInput(true);
  }, WIZARD_DELAYS.LONG);
};

export const handleEndTimeInput = (
  time: string,
  deps: AvailabilityStepDependencies,
) => {
  const { addBotMessage, setAwaitingUserInput, advanceToStep } = deps;

  // Validação: remove espaços
  const trimmedTime = time.trim();

  // Validação: formato vazio
  if (!trimmedTime) {
    setTimeout(() => {
      addBotMessage(
        "❌ O horário não pode estar vazio. Digite no formato HH:MM (ex: 18:00)",
        {
          wizardType: "error",
          delay: WIZARD_DELAYS.MEDIUM,
        },
      );
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.MEDIUM);
    return;
  }

  // Validação: formato HH:MM
  if (!isValidTimeFormat(trimmedTime)) {
    setTimeout(() => {
      addBotMessage("❌ Formato inválido. Use HH:MM (ex: 18:00 ou 17:30)", {
        wizardType: "error",
        delay: WIZARD_DELAYS.MEDIUM,
      });
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.MEDIUM);
    return;
  }

  const startTime = (
    globalThis as typeof globalThis & { __temp_start_time: string }
  ).__temp_start_time;

  // Validação: horário de término deve ser maior que o de início
  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [endHours, endMinutes] = trimmedTime.split(":").map(Number);
  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;

  if (endTotalMinutes <= startTotalMinutes) {
    setTimeout(() => {
      addBotMessage(
        `❌ O horário de término (${trimmedTime}) deve ser maior que o horário de início (${startTime}).\n\nPor favor, digite um horário válido.`,
        {
          wizardType: "error",
          delay: WIZARD_DELAYS.MEDIUM,
        },
      );
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.MEDIUM);
    return;
  }

  // Validação: duração mínima de 1 hora
  const durationMinutes = endTotalMinutes - startTotalMinutes;
  if (durationMinutes < 60) {
    setTimeout(() => {
      addBotMessage(
        `❌ A duração mínima é de 1 hora. O período entre ${startTime} e ${trimmedTime} é muito curto.\n\nPor favor, digite um horário de término maior.`,
        {
          wizardType: "error",
          delay: WIZARD_DELAYS.MEDIUM,
        },
      );
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.MEDIUM);
    return;
  }

  if (currentDay !== null) {
    currentAvailabilities.push({
      day_of_week: currentDay,
      start_time: startTime,
      end_time: trimmedTime,
    });

    const dayLabel = DAYS.find((d) => d.value === currentDay)?.label || "";

    // Reset currentDay para a próxima iteração
    currentDay = null;

    setTimeout(() => {
      advanceToStep("availability_confirmation", 0);
      addBotMessage(
        `✅ ${dayLabel}: ${startTime} às ${trimmedTime}\n\nDeseja adicionar outro dia?`,
        {
          wizardType: "question",
          delay: WIZARD_DELAYS.MEDIUM,
          actions: [
            {
              label: "Sim, Adicionar Mais",
              onClick: () => continueAddingDays(deps),
              variant: "primary" as const,
            },
            {
              label: "Não, Finalizar",
              onClick: () => void saveAvailabilityAndContinue(deps),
              variant: "secondary" as const,
            },
          ],
        },
      );
    }, WIZARD_DELAYS.MEDIUM);
  }
};

const continueAddingDays = (deps: AvailabilityStepDependencies) => {
  const { addUserMessage, addBotMessage } = deps;

  addUserMessage("Sim, adicionar mais");

  // Filtra os dias que já foram adicionados
  const addedDays = new Set(currentAvailabilities.map((a) => a.day_of_week));
  const remainingDays = DAYS.filter((day) => !addedDays.has(day.value));

  // Se não há mais dias disponíveis
  if (remainingDays.length === 0) {
    setTimeout(() => {
      addBotMessage(
        "Você já adicionou todos os dias da semana! 🎉\n\nVamos finalizar a configuração de disponibilidade.",
        { wizardType: "info", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);

    setTimeout(() => {
      void saveAvailabilityAndContinue(deps);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Separa dias úteis e fins de semana restantes
  const remainingWeekdays = remainingDays.filter((day) => day.value < 5);
  const remainingWeekends = remainingDays.filter((day) => day.value >= 5);

  setTimeout(() => {
    if (remainingWeekdays.length > 0) {
      addBotMessage(
        "Ótimo! Quais outros dias você atende? Aqui estão os dias ainda não configurados:",
        {
          wizardType: "question",
          delay: WIZARD_DELAYS.MEDIUM,
          actions: remainingWeekdays.map((day) => ({
            label: day.short,
            onClick: () => handleDaySelection(day.value, deps),
            variant: "secondary" as const,
          })),
        },
      );
    }
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(
    () => {
      const actions = [];

      // Adiciona fins de semana se houver
      if (remainingWeekends.length > 0) {
        actions.push(
          ...remainingWeekends.map((day) => ({
            label: day.label,
            onClick: () => handleDaySelection(day.value, deps),
            variant: "secondary" as const,
          })),
        );
      }

      // Sempre adiciona opção de finalizar
      actions.push({
        label: "Finalizar Configuração",
        onClick: () => void saveAvailabilityAndContinue(deps),
        variant: "primary" as const,
      });

      // Se há fins de semana restantes, mostra uma mensagem específica
      if (remainingWeekends.length > 0) {
        addBotMessage("Fins de semana:", {
          wizardType: "question",
          delay: WIZARD_DELAYS.MEDIUM,
          actions,
        });
      } else if (remainingWeekdays.length === 0) {
        // Se só havia fins de semana e todos já foram adicionados
        addBotMessage("Deseja finalizar a configuração?", {
          wizardType: "question",
          delay: WIZARD_DELAYS.MEDIUM,
          actions,
        });
      }
    },
    remainingWeekdays.length > 0 ? WIZARD_DELAYS.LONG : WIZARD_DELAYS.MEDIUM,
  );
};

const isValidTimeFormat = (time: string): boolean => {
  const timeRegex = /^([0-1]?\d|2[0-3]):[0-5]\d$/;
  return timeRegex.test(time);
};

const handleSkipAvailability = (deps: AvailabilityStepDependencies) => {
  const { addUserMessage, addBotMessage, advanceToStep } = deps;

  addUserMessage("Pular por enquanto");

  setTimeout(() => {
    addBotMessage(
      "Sem problemas! Você pode configurar seus horários depois no painel.",
      { wizardType: "info", delay: WIZARD_DELAYS.MEDIUM },
    );
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(() => {
    advanceToStep("links", 0);
    // Importar e chamar o próximo step
    void import("./linksStep").then((module) => {
      // Adaptar para o formato esperado
      const adapted = {
        ...deps,
        showFinishedStep: () => {
          void import("./finishedStep").then((finishedModule) => {
            void finishedModule.showFinishedStep(deps as never);
          });
        },
      };
      module.startLinksConfiguration(adapted);
    });
  }, WIZARD_DELAYS.LONG);
};

const saveAvailabilityAndContinue = async (
  deps: AvailabilityStepDependencies,
) => {
  const {
    userId,
    addUserMessage,
    addBotMessage,
    advanceToStep,
    setIsProcessing,
  } = deps;

  addUserMessage("Não, finalizar");

  if (currentAvailabilities.length === 0) {
    handleSkipAvailability(deps);
    return;
  }

  setTimeout(() => {
    addBotMessage("Perfeito! Salvando sua disponibilidade...", {
      wizardType: "info",
      delay: WIZARD_DELAYS.MEDIUM,
    });
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(async () => {
    if (!userId) {
      addBotMessage(
        "❌ Erro: usuário não identificado. Vamos pular por enquanto.",
        {
          wizardType: "error",
          delay: WIZARD_DELAYS.MEDIUM,
        },
      );
      setTimeout(() => {
        handleSkipAvailability(deps);
      }, WIZARD_DELAYS.LONG);
      return;
    }

    setIsProcessing(true);

    try {
      const result = await appointment.setAvailability(
        String(userId),
        currentAvailabilities,
        true,
      );

      if (result) {
        addBotMessage(
          `✅ Horários salvos com sucesso!\n\nTotal: ${currentAvailabilities.length} horário(s) configurado(s).`,
          { wizardType: "confirmation", delay: WIZARD_DELAYS.MEDIUM },
        );

        setTimeout(() => {
          addBotMessage("Agora vamos aos seus links sociais! 🔗", {
            wizardType: "info",
            delay: WIZARD_DELAYS.MEDIUM,
          });
        }, WIZARD_DELAYS.LONG);

        setTimeout(() => {
          advanceToStep("links", 0);
          // Importar e chamar o próximo step
          void import("./linksStep").then((module) => {
            const adapted = {
              ...deps,
              showFinishedStep: () => {
                void import("./finishedStep").then((finishedModule) => {
                  void finishedModule.showFinishedStep(deps as never);
                });
              },
            };
            module.startLinksConfiguration(adapted);
          });
        }, WIZARD_DELAYS.VERY_LONG);
      } else {
        addBotMessage("❌ Erro ao salvar horários. Vamos pular por enquanto.", {
          wizardType: "error",
          delay: WIZARD_DELAYS.MEDIUM,
        });
        handleSkipAvailability(deps);
      }
    } catch (error) {
      console.error("Erro ao salvar disponibilidade:", error);
      addBotMessage("❌ Erro ao salvar horários. Vamos pular por enquanto.", {
        wizardType: "error",
        delay: WIZARD_DELAYS.MEDIUM,
      });
      handleSkipAvailability(deps);
    } finally {
      setIsProcessing(false);
    }
  }, WIZARD_DELAYS.LONG);
};

// Removida - não é mais necessária, importamos diretamente do linksStep
